import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { patientsApi } from '@/api/endpoints/patients.api';
import type { PatientAllergy, CreateAllergyRequest } from '@/types/patient.types';

const allergySchema = z.object({
  allergen: z.string().min(1, 'Alérgeno é obrigatório'),
  allergenType: z.string().optional(),
  reaction: z.string().optional(),
  severity: z.string().optional(),
  confirmed: z.boolean().optional(),
});

type AllergyFormData = z.infer<typeof allergySchema>;

interface AddAllergyDialogProps {
  patientId: string;
  onSuccess: (allergy: PatientAllergy) => void;
}

const ALLERGEN_TYPES = [
  { value: 'MEDICATION', label: 'Medicamento' },
  { value: 'FOOD', label: 'Alimento' },
  { value: 'ENVIRONMENTAL', label: 'Ambiental' },
  { value: 'OTHER', label: 'Outro' },
];

const SEVERITY_LEVELS = [
  { value: 'MILD', label: 'Leve' },
  { value: 'MODERATE', label: 'Moderada' },
  { value: 'SEVERE', label: 'Grave' },
  { value: 'LIFE_THREATENING', label: 'Risco de Vida' },
];

export function AddAllergyDialog({ patientId, onSuccess }: AddAllergyDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');
  const [confirmed, setConfirmed] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AllergyFormData>({
    resolver: zodResolver(allergySchema),
    defaultValues: {
      allergen: '',
      allergenType: '',
      reaction: '',
      severity: '',
      confirmed: false,
    },
  });

  const onSubmit = async (data: AllergyFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const request: CreateAllergyRequest = {
        allergen: data.allergen,
        allergenType: selectedType as 'MEDICATION' | 'FOOD' | 'ENVIRONMENTAL' | 'OTHER' | undefined,
        reaction: data.reaction || undefined,
        severity: selectedSeverity as 'MILD' | 'MODERATE' | 'SEVERE' | 'LIFE_THREATENING' | undefined,
        confirmed: confirmed,
      };

      const allergy = await patientsApi.addAllergy(patientId, request);
      onSuccess(allergy);
      reset();
      setSelectedType('');
      setSelectedSeverity('');
      setConfirmed(false);
      setOpen(false);
    } catch (err) {
      console.error('Erro ao adicionar alergia:', err);
      setError('Erro ao adicionar alergia. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Alergia
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Adicionar Alergia</DialogTitle>
          <DialogDescription>
            Registre uma nova alergia ou intolerância do paciente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="allergen">Alérgeno *</Label>
            <Input
              id="allergen"
              placeholder="Ex: Dipirona, Amendoim, Látex"
              {...register('allergen')}
            />
            {errors.allergen && (
              <p className="text-sm text-destructive">{errors.allergen.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="allergenType">Tipo de Alérgeno</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {ALLERGEN_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="severity">Gravidade</Label>
              <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a gravidade" />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_LEVELS.map((level) => (
                    <SelectItem key={level.value} value={level.value}>
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reaction">Reação</Label>
            <Textarea
              id="reaction"
              placeholder="Descreva a reação alérgica observada..."
              {...register('reaction')}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="confirmed"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <Label htmlFor="confirmed" className="text-sm font-normal">
              Alergia confirmada por teste ou histórico médico
            </Label>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
